import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import SquarespacePageHeader from "../components/SquarespacePageHeader";

export default function SquarespaceDocs() {
  const navigate = useNavigate();

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component="a"
                  href="/dashboard"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/dashboard");
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                  onClick={() => navigate("/dashboard/squarespace")}
                >
                  Squarespace
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Documentation
                </Typography>
              </Breadcrumbs>
            </Box>

            <SquarespacePageHeader
              title="Squarespace Integration Docs"
              description="Customer, website, and payload flow — how data is stored across layers"
              icon={<MenuBookIcon sx={{ color: "primary.main" }} />}
            />

            {/* Customers */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Customer Logic
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Squarespace websites are attached to a <strong>contact</strong>. Each Penn Station contact can have a
                  linked Squarespace customer (account). The Squarespace Reseller API manages customers separately from
                  our database — we store the link in our contact record.
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  When a website order is created, we either reuse an existing Squarespace customer (by email) or create
                  a new one via the Reseller API. The resulting customer ID is stored immediately so retries do not
                  create duplicate Squarespace accounts.
                </Typography>
                <Box
                  component="code"
                  sx={{
                    display: "block",
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  }}
                >
                  contact.meta_data.squarespace_customer_id
                </Box>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="text.secondary">
                  Stores the Squarespace customer UUID for this contact.
                </Typography>
              </CardContent>
            </Card>

            {/* Websites */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Websites Logic
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  A Squarespace website order is created via the Reseller API using:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 2 }}>
                  <li>
                    <strong>customerId</strong> — from contact.meta_data.squarespace_customer_id (or newly created)
                  </li>
                  <li>
                    <strong>pricingId</strong> — from the subscription plan&apos;s meta_data.squarespace_pricing_id
                  </li>
                  <li>
                    <strong>title</strong> — website title from the payload
                  </li>
                  <li>
                    <strong>domains</strong> — optional, from payload
                  </li>
                </Typography>
                <Typography variant="body2">
                  Websites appear in Squarespace after the order succeeds. We store the order ID on the subscription
                  for traceability.
                </Typography>
              </CardContent>
            </Card>

            {/* Payload Flow */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Creating an Order via Payload
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  An inbound payload (e.g. from the USFS order process) triggers the job-payload-process flow. The
                  payload is stored in <strong>inbound_payloads</strong>, then Inngest runs the job.
                </Typography>
                <Typography variant="body2" fontWeight="medium" sx={{ mt: 2, mb: 1 }}>
                  Job steps:
                </Typography>
                <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
                  <li>
                    <Typography variant="body2">Validate required fields (email, organization_id, subscription_plan_id, ipAddress, title, firstName, lastName)</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Verify organization exists</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Verify subscription plan exists and has squarespace_pricing_id in meta_data</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Find or create contact for the organization (by email)</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Create subscription (pricingId from plan → subscription.meta_data)</Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      Create Squarespace order: use contact.meta_data.squarespace_customer_id if present, else
                      create/find SS customer and store in contact.meta_data
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">Store squarespace_order_id and squarespace_customer_id in subscription.meta_data</Typography>
                  </li>
                </ol>
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Submit payloads via <strong>Submit Order</strong> or <strong>Payloads</strong> → create payload → trigger.
                </Typography>
              </CardContent>
            </Card>

            {/* Data Storage Layers */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Data Storage by Layer
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  How Squarespace-related data is stored across our entities:
                </Typography>
                <Table size="small" sx={{ "& td": { verticalAlign: "top", py: 1 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell>Field</TableCell>
                      <TableCell>Purpose</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "monospace" }}>contacts</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>meta_data.squarespace_customer_id</TableCell>
                      <TableCell>Links contact to Squarespace customer; reused on retries</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "monospace" }}>subscriptions</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>meta_data.squarespace_customer_id</TableCell>
                      <TableCell>Customer ID used for the website order</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "monospace" }}>subscriptions</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>meta_data.squarespace_order_id</TableCell>
                      <TableCell>Squarespace order UUID for traceability</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "monospace" }}>subscriptions</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>meta_data.pricingId</TableCell>
                      <TableCell>Squarespace pricing ID (from subscription_plan.meta_data.squarespace_pricing_id)</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "monospace" }}>subscription_plans</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>meta_data.squarespace_pricing_id</TableCell>
                      <TableCell>Maps this plan to a Squarespace pricing SKU</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontFamily: "monospace" }}>inbound_payloads</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>payload</TableCell>
                      <TableCell>Raw payload JSON; meta_data stores squarespaceOrderId, subscriptionId, etc.</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Quick reference */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Quick links
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  onClick={() => navigate("/dashboard/squarespace/payloads")}
                >
                  Payloads
                </Typography>
                <Typography variant="body2" color="text.secondary">·</Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  onClick={() => navigate("/dashboard/squarespace/process-order")}
                >
                  Submit Order
                </Typography>
                <Typography variant="body2" color="text.secondary">·</Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  onClick={() => navigate("/dashboard/contacts")}
                >
                  Contacts
                </Typography>
                <Typography variant="body2" color="text.secondary">·</Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                  onClick={() => navigate("/dashboard/subscription-plans")}
                >
                  Subscription Plans
                </Typography>
              </Box>
            </Paper>
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
